import { CalendarIcon, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import type { DateRange } from "react-day-picker";

import { Button } from "../button";
import { Calendar } from "../calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../popover";
import { cn, formatDate } from "@tellescope/utils";
import {
  dateRangeWrapperVariants,
  dateRangeButtonVariants,
  dateRangeIconVariants,
  dateRangeClearVariants,
  DATE_FORMAT_BY_SIZE,
  BUTTON_SIZE_BY_SIZE,
} from "./styles";

export interface DateRangeFieldValue {
  from?: number;
  to?: number;
}

export interface DateRangeFieldProps {
  label?: string;
  value: DateRangeFieldValue;
  onChange: (value: DateRangeFieldValue) => void;
  placeholder?: string;
  className?: string;
  size?: "default" | "sm" | "xs";
  disabled?: boolean;
}

export const DateRangeField = ({
  label,
  value,
  onChange,
  placeholder,
  className,
  size = "default",
  disabled,
}: DateRangeFieldProps) => {
  const { t } = useTranslation("ui");
  const [open, setOpen] = useState(false);

  const fromDate = value.from ? new Date(value.from) : undefined;
  const toDate = value.to ? new Date(value.to) : undefined;

  const selected: DateRange | undefined = fromDate || toDate ? { from: fromDate, to: toDate } : undefined;

  const handleSelect = (range: DateRange | undefined) => {
    onChange({
      from: range?.from?.getTime(),
      to: range?.to?.getTime(),
    });
  };

  const hasValue = value.from !== undefined || value.to !== undefined;

  const dateFormat = DATE_FORMAT_BY_SIZE[size];
  const buttonLabel = hasValue
    ? `${fromDate ? formatDate(fromDate, dateFormat) : "..."} — ${toDate ? formatDate(toDate, dateFormat) : "..."}`
    : placeholder || t("Select date range");

  return (
    <div className="space-y-1.5">
      {!!label && <span className="text-sm font-medium">{label}</span>}
      <div className={dateRangeWrapperVariants({ size })}>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild disabled={disabled}>
            <Button
              variant="outline"
              size={BUTTON_SIZE_BY_SIZE[size]}
              className={cn(dateRangeButtonVariants({ size }), !hasValue && "text-muted-foreground", className)}
              disabled={disabled}
            >
              <CalendarIcon className={dateRangeIconVariants({ size })} />
              {buttonLabel}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="p-0 w-[500px]" align="start">
            <Calendar
              mode="range"
              selected={selected}
              onSelect={handleSelect}
              numberOfMonths={2}
              defaultMonth={fromDate}
              className="w-500"
            />
          </PopoverContent>
        </Popover>
        {hasValue && (
          <button
            type="button"
            className="text-muted-foreground hover:text-foreground focus:outline-none"
            onClick={() => onChange({})}
            aria-label={t("Clear date range")}
          >
            <X className={dateRangeClearVariants({ size })} />
          </button>
        )}
      </div>
    </div>
  );
};
